import {
  formatPageUpdateValue,
  isRecord,
  parseJsonIfString,
  type FormUiFields,
  type FormUiWriteResult,
  type PageUpdatePreviewRow,
} from '@elementary/common-ui/components/AiChat';
import { WarehouseType } from '@elementary/common-ui/types';
import { UseFormReturn } from 'react-hook-form';
import {
  AssetSourceFormData,
  BigQueryAuthMethod,
  EnvironmentForm,
  WarehouseDbFilterFormData,
} from 'shared/types';
import {
  BIGQUERY_SERVICE_ACCOUNT_DOCS_URL,
  BIGQUERY_WIF_DOCS_URL,
  CONNECTION_STEP_INDEX,
  CONNECTION_STEP_TITLES,
  CONTENT_STEP_INDEX,
  DBT_PACKAGE_DOCS_URL,
  ELEMENTARY_IP,
  ENVIRONMENT_CONTENT_STEP_TITLE,
  ENVIRONMENT_DRAWER_ACTIONS,
} from '../constants';
import { getAssetSourceRules, getDefaultWarehouseAssetSource } from '../utils';
import {
  ASSET_SOURCE_PREVIEW_LABEL,
  ASSET_SOURCES_FIELD,
  CONNECTION_STEP_DESCRIPTION,
  CONTENT_STEP_DESCRIPTION,
  ENVIRONMENT_DETAILS_FIELDS,
  SSH_TUNNEL_FIELDS,
  SSH_TUNNEL_NOTE,
  SSH_TUNNEL_WAREHOUSE_TYPES,
  WAREHOUSE_FIELDS,
  WAREHOUSE_TYPE_FIELD,
} from './constants';
import { EnvironmentFormStep, EnvironmentFormWriteResult } from './types';

export const getEnvironmentFormSteps = (
  isEditMode: boolean,
): EnvironmentFormStep[] => [
  {
    index: CONNECTION_STEP_INDEX,
    name: isEditMode
      ? CONNECTION_STEP_TITLES.edit
      : CONNECTION_STEP_TITLES.create,
    description: CONNECTION_STEP_DESCRIPTION,
  },
  {
    index: CONTENT_STEP_INDEX,
    name: ENVIRONMENT_CONTENT_STEP_TITLE,
    description: CONTENT_STEP_DESCRIPTION,
  },
];

export const isWarehouseType = (value: unknown): value is WarehouseType =>
  typeof value === 'string' && value in WAREHOUSE_FIELDS;

const isSavedAssetSource = (
  source: AssetSourceFormData,
  existingAssetSourceIds: string[],
) => source.id != null && existingAssetSourceIds.includes(source.id);

const isBlank = (value: string | undefined) => !value || value.trim() === '';

const readOptionalString = (
  item: Record<string, unknown>,
  key: string,
): EnvironmentFormWriteResult<string | undefined> => {
  const value = item[key];
  if (value === undefined || value === null) {
    return { ok: true, value: undefined };
  }
  if (typeof value === 'string') {
    return { ok: true, value };
  }
  if (typeof value === 'number') {
    return { ok: true, value: String(value) };
  }
  return { ok: false, reason: `${key} must be a string` };
};

const normalizeSchemas = (
  value: unknown,
): EnvironmentFormWriteResult<string[]> => {
  const parsed = parseJsonIfString(value);
  if (parsed === undefined || parsed === null) {
    return { ok: true, value: [] };
  }
  const list = typeof parsed === 'string' ? parsed.split(',') : parsed;
  if (
    !Array.isArray(list) ||
    !list.every((schema) => typeof schema === 'string')
  ) {
    return { ok: false, reason: 'schemas must be a list of strings' };
  }
  return {
    ok: true,
    value: list.map((schema) => schema.trim()).filter((schema) => !!schema),
  };
};

const normalizeDbFilters = (
  value: unknown,
  hasWarehouseSchemas: boolean,
  warehouseType: WarehouseType,
): EnvironmentFormWriteResult<WarehouseDbFilterFormData[]> => {
  if (!Array.isArray(value) || value.length === 0) {
    return {
      ok: false,
      reason: 'dbFilters must be a non-empty list of { database, schemas }',
    };
  }
  const filters: WarehouseDbFilterFormData[] = [];
  for (const [index, filter] of value.entries()) {
    if (!isRecord(filter)) {
      return { ok: false, reason: `dbFilters[${index}] must be an object` };
    }
    const database = readOptionalString(filter, 'database');
    if (!database.ok) {
      return { ok: false, reason: `dbFilters[${index}].${database.reason}` };
    }
    if (isBlank(database.value)) {
      return { ok: false, reason: `dbFilters[${index}].database is required` };
    }
    const schemas = normalizeSchemas(filter.schemas);
    if (!schemas.ok) {
      return { ok: false, reason: `dbFilters[${index}].${schemas.reason}` };
    }
    if (!hasWarehouseSchemas && schemas.value.length > 0) {
      return {
        ok: false,
        reason: `${warehouseType} warehouse sources filter by database only; omit schemas`,
      };
    }
    filters.push({ database: database.value ?? '', schemas: schemas.value });
  }
  return { ok: true, value: filters };
};

const normalizeAssetSourceWrite = (
  item: unknown,
  current: AssetSourceFormData | undefined,
  isExisting: boolean,
  warehouseType: WarehouseType,
): EnvironmentFormWriteResult<AssetSourceFormData> => {
  if (!isRecord(item)) {
    return { ok: false, reason: 'Must be an object' };
  }
  const type = item.type ?? current?.type;
  if (type !== 'dbt' && type !== 'warehouse') {
    return { ok: false, reason: 'type must be "dbt" or "warehouse"' };
  }
  if (isExisting && current && type !== current.type) {
    return {
      ok: false,
      reason: `Saved ${current.type} sources cannot change type`,
    };
  }
  const name = readOptionalString(item, 'name');
  if (!name.ok) {
    return name;
  }
  const idProp = current?.id ? { id: current.id } : {};
  const rules = getAssetSourceRules(warehouseType);

  if (type === 'dbt') {
    const currentDbt = current?.type === 'dbt' ? current : undefined;
    const database = readOptionalString(item, 'database');
    const schema = readOptionalString(item, 'schema');
    if (!database.ok) {
      return database;
    }
    if (!schema.ok) {
      return schema;
    }
    if (!rules.hasDatabase && database.value) {
      return {
        ok: false,
        reason: `${warehouseType} dbt sources have no database; omit database`,
      };
    }
    if (
      isExisting &&
      currentDbt &&
      ((database.value !== undefined &&
        database.value !== currentDbt.database) ||
        (schema.value !== undefined && schema.value !== currentDbt.schema))
    ) {
      return {
        ok: false,
        reason: 'database and schema of a saved dbt source cannot change',
      };
    }
    const source: AssetSourceFormData = {
      ...idProp,
      type: 'dbt',
      name: name.value ?? currentDbt?.name ?? '',
      database: rules.hasDatabase
        ? database.value ?? currentDbt?.database ?? ''
        : undefined,
      schema: schema.value ?? currentDbt?.schema ?? '',
    };
    if (isBlank(source.name)) {
      return { ok: false, reason: 'name is required' };
    }
    if (rules.isDbtDatabaseRequired && isBlank(source.database)) {
      return { ok: false, reason: 'database is required' };
    }
    if (isBlank(source.schema)) {
      return { ok: false, reason: 'schema is required' };
    }
    return { ok: true, value: source };
  }

  const currentWarehouse = current?.type === 'warehouse' ? current : undefined;
  const dbFilters = normalizeDbFilters(
    parseJsonIfString(item.dbFilters) ??
      currentWarehouse?.dbFilters ??
      getDefaultWarehouseAssetSource().dbFilters,
    rules.hasWarehouseSchemas,
    warehouseType,
  );
  if (!dbFilters.ok) {
    return dbFilters;
  }
  const sourceName = name.value ?? currentWarehouse?.name ?? '';
  if (!isExisting && isBlank(sourceName)) {
    return { ok: false, reason: 'name is required' };
  }
  return {
    ok: true,
    value: {
      ...idProp,
      type: 'warehouse',
      name: sourceName,
      dbFilters: dbFilters.value,
    },
  };
};

export const normalizeAssetSourcesWrite = (
  value: unknown,
  {
    currentSources,
    existingAssetSourceIds,
    warehouseType,
  }: {
    currentSources: AssetSourceFormData[];
    existingAssetSourceIds: string[];
    warehouseType: WarehouseType;
  },
): EnvironmentFormWriteResult<AssetSourceFormData[]> => {
  const parsed = parseJsonIfString(value);
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return {
      ok: false,
      reason: 'Must be a non-empty list of asset sources',
    };
  }
  const seenIds = new Set<string>();
  const sources: AssetSourceFormData[] = [];
  for (const [index, item] of parsed.entries()) {
    const id = isRecord(item) && typeof item.id === 'string' ? item.id : null;
    const current = id
      ? currentSources.find((source) => source.id === id)
      : undefined;
    if (id && !current) {
      return {
        ok: false,
        reason: `assetSources[${index}].id "${id}" is not in the form; omit id to add a new source`,
      };
    }
    if (id && seenIds.has(id)) {
      return {
        ok: false,
        reason: `assetSources[${index}].id "${id}" appears more than once`,
      };
    }
    if (id) {
      seenIds.add(id);
    }
    const normalized = normalizeAssetSourceWrite(
      item,
      current,
      !!current && isSavedAssetSource(current, existingAssetSourceIds),
      warehouseType,
    );
    if (!normalized.ok) {
      return {
        ok: false,
        reason: `assetSources[${index}]: ${normalized.reason}`,
      };
    }
    sources.push(normalized.value);
  }
  return { ok: true, value: sources };
};

const writeAssetSources = (
  value: unknown,
  methods: UseFormReturn<EnvironmentForm>,
  existingAssetSourceIds: string[],
): FormUiWriteResult => {
  const { warehouseType, assetSources } = methods.getValues();
  if (!isWarehouseType(warehouseType)) {
    return { ok: false, reason: 'Select a data warehouse first' };
  }
  const result = normalizeAssetSourcesWrite(value, {
    currentSources: assetSources ?? [],
    existingAssetSourceIds,
    warehouseType,
  });
  if (!result.ok) {
    return result;
  }
  methods.setValue('assetSources', result.value, { shouldDirty: true });
  return { ok: true };
};

const formatAssetSource = (source: unknown): string => {
  if (!isRecord(source)) {
    return JSON.stringify(source);
  }
  const name =
    typeof source.name === 'string' && source.name
      ? source.name
      : 'Untitled source';
  if (source.type === 'dbt') {
    const location = [source.database, source.schema]
      .filter((part) => typeof part === 'string' && part)
      .join('.');
    return location ? `${name} (dbt, ${location})` : `${name} (dbt)`;
  }
  const filters = Array.isArray(source.dbFilters) ? source.dbFilters : [];
  const locations = filters.filter(isRecord).map((filter) => {
    const schemas = Array.isArray(filter.schemas) ? filter.schemas : [];
    const database =
      typeof filter.database === 'string' && filter.database
        ? filter.database
        : 'no database';
    return schemas.length > 0
      ? `${database} [${schemas.join(', ')}]`
      : `${database} [all]`;
  });
  return locations.length > 0
    ? `${name} (warehouse, ${locations.join('; ')})`
    : `${name} (warehouse)`;
};

const previewAssetSources = (value: unknown): PageUpdatePreviewRow[] => {
  const parsed = parseJsonIfString(value);
  if (!Array.isArray(parsed)) {
    return [
      {
        field: 'assetSources',
        label: ASSET_SOURCES_FIELD.label,
        value: formatPageUpdateValue(value) ?? '',
      },
    ];
  }
  return parsed.map((source, index) => ({
    field: `assetSources.${index}`,
    label: `${ASSET_SOURCE_PREVIEW_LABEL} ${index + 1}`,
    value: formatPageUpdateValue(formatAssetSource(source)) ?? '',
  }));
};

const withVisibility = (
  fields: FormUiFields<EnvironmentForm>,
  isGroupVisible: (values: Record<string, unknown>) => boolean,
): FormUiFields<EnvironmentForm> =>
  Object.fromEntries(
    Object.entries(fields).map(([name, field]) => [
      name,
      field && {
        ...field,
        isVisible: (values: Record<string, unknown>) =>
          isGroupVisible(values) && (field.isVisible?.(values) ?? true),
      },
    ]),
  );

export const getEnvironmentFormFields = ({
  currentStep,
  existingAssetSourceIds,
}: {
  currentStep: number;
  existingAssetSourceIds: string[];
}): FormUiFields<EnvironmentForm> => {
  const isConnectionStep = currentStep === CONNECTION_STEP_INDEX;
  const warehouseFields = Object.entries(WAREHOUSE_FIELDS).reduce<
    FormUiFields<EnvironmentForm>
  >(
    (all, [warehouseType, fields]) => ({
      ...all,
      ...withVisibility(
        fields,
        (values) => isConnectionStep && values.warehouseType === warehouseType,
      ),
    }),
    {},
  );
  return {
    ...withVisibility(
      { ...ENVIRONMENT_DETAILS_FIELDS, warehouseType: WAREHOUSE_TYPE_FIELD },
      () => isConnectionStep,
    ),
    ...warehouseFields,
    ...withVisibility(
      SSH_TUNNEL_FIELDS,
      (values) =>
        isConnectionStep &&
        SSH_TUNNEL_WAREHOUSE_TYPES.some(
          (type) => type === values.warehouseType,
        ),
    ),
    assetSources: {
      ...ASSET_SOURCES_FIELD,
      isVisible: () => !isConnectionStep,
      onWrite: (value, methods) =>
        writeAssetSources(value, methods, existingAssetSourceIds),
      preview: previewAssetSources,
    },
  };
};

export const getEnvironmentFormHint = ({
  isEditMode,
  stepIndex,
}: {
  isEditMode: boolean;
  stepIndex: number;
}) => {
  const {
    continue: continueLabel,
    saveAndSync,
    saveChanges,
    configureAssetSources,
  } = ENVIRONMENT_DRAWER_ACTIONS;
  if (isEditMode) {
    return stepIndex === CONNECTION_STEP_INDEX
      ? `The user saves with "${saveChanges}" in the drawer header. "${configureAssetSources}" opens the ${ENVIRONMENT_CONTENT_STEP_TITLE} step.`
      : `The user saves with "${saveChanges}" in the drawer header.`;
  }
  return stepIndex === CONNECTION_STEP_INDEX
    ? `When the required fields are filled, the user clicks "${continueLabel}" to move to the ${ENVIRONMENT_CONTENT_STEP_TITLE} step.`
    : `The user clicks "${saveAndSync}" to create the environment. Elementary then connects to the warehouse; if that fails, submitError explains why.`;
};

export const getEnvironmentFormNotes = (
  stepIndex: number,
  values: Record<string, unknown>,
) => {
  if (stepIndex === CONTENT_STEP_INDEX) {
    return [
      `dbt project sources require the Elementary dbt package to be installed and run: ${DBT_PACKAGE_DOCS_URL}`,
    ];
  }
  const notes = [
    `If the warehouse restricts access with an IP allowlist, Elementary's IP ${ELEMENTARY_IP} must be allowed.`,
  ];
  if (values.sshEnabled === true) {
    notes.push(SSH_TUNNEL_NOTE);
  }
  if (values.warehouseType === 'bigquery') {
    notes.push(
      values.bigqueryAuthMethod === BigQueryAuthMethod.WIF
        ? `Workload Identity Federation setup: ${BIGQUERY_WIF_DOCS_URL}`
        : `Service account setup: ${BIGQUERY_SERVICE_ACCOUNT_DOCS_URL}`,
    );
  }
  return notes;
};
